export type WeaponId = 'pistol' | 'shotgun' | 'sawedoff' | 'mp5' | 'ak47' | 'punch' | 'gl' | 'rpg';

export interface WeaponDef {
  id: WeaponId;
  name: string;
  magSize: number;
  reserveStart: number;
  /** Seconds between shots */
  fireInterval: number;
  reloadTime: number;
  /** Automatic pump delay after shotgun shot */
  pumpTime: number;
  hipSpreadDeg: number;
  adsSpreadDeg: number;
  pellets: number;
  damage: number;
  adsFov: number;
  adsSensScale: number;
  recoilKick: number;
  recoilRecovery: number;
  /** true = hold fire for auto */
  automatic: boolean;
  requiresReload: boolean;
  requiresPump: boolean;
}

export const WEAPONS: Record<WeaponId, WeaponDef> = {
  pistol: {
    id: 'pistol',
    name: 'Pistol',
    magSize: 12,
    reserveStart: 48,
    fireInterval: 0.04,
    reloadTime: 1.25,
    pumpTime: 0,
    hipSpreadDeg: 2.8,
    adsSpreadDeg: 0.55,
    pellets: 1,
    damage: 22,
    adsFov: 62,
    adsSensScale: 0.55,
    recoilKick: 0.12,
    recoilRecovery: 9,
    automatic: false,
    requiresReload: true,
    requiresPump: false,
  },
  shotgun: {
    id: 'shotgun',
    name: 'Shotgun',
    magSize: 12,
    reserveStart: 0,
    fireInterval: 0.85,
    reloadTime: 0,
    pumpTime: 0.62,
    hipSpreadDeg: 6.5,
    adsSpreadDeg: 2.8,
    pellets: 8,
    damage: 14,
    adsFov: 68,
    adsSensScale: 0.6,
    recoilKick: 0.11,
    recoilRecovery: 8,
    automatic: false,
    requiresReload: false,
    requiresPump: true,
  },
  sawedoff: {
    id: 'sawedoff',
    name: 'Double Barrel',
    magSize: 2,
    reserveStart: 8,
    fireInterval: 0.85,
    reloadTime: 2.2,
    pumpTime: 0,
    hipSpreadDeg: 8.5,
    adsSpreadDeg: 4.2,
    pellets: 16,
    damage: 14,
    adsFov: 70,
    adsSensScale: 0.65,
    recoilKick: 0.22,
    recoilRecovery: 6.5,
    automatic: false,
    requiresReload: true,
    requiresPump: false,
  },
  mp5: {
    id: 'mp5',
    name: 'MP5',
    magSize: 30,
    reserveStart: 60,
    fireInterval: 0.09,
    reloadTime: 1.35,
    pumpTime: 0,
    hipSpreadDeg: 3.2,
    adsSpreadDeg: 0.9,
    pellets: 1,
    damage: 16,
    adsFov: 60,
    adsSensScale: 0.55,
    recoilKick: 0.022,
    recoilRecovery: 16,
    automatic: true,
    requiresReload: true,
    requiresPump: false,
  },
  ak47: {
    id: 'ak47',
    name: 'AK-47',
    magSize: 30,
    reserveStart: 90,
    fireInterval: 0.11,
    reloadTime: 1.55,
    pumpTime: 0,
    hipSpreadDeg: 3.8,
    adsSpreadDeg: 1.15,
    pellets: 1,
    damage: 20,
    adsFov: 58,
    adsSensScale: 0.5,
    recoilKick: 0.03,
    recoilRecovery: 14,
    automatic: true,
    requiresReload: true,
    requiresPump: false,
  },
  punch: {
    id: 'punch',
    name: 'Fists',
    magSize: 0,
    reserveStart: 0,
    fireInterval: 0.36,
    reloadTime: 0,
    pumpTime: 0,
    hipSpreadDeg: 0,
    adsSpreadDeg: 0,
    pellets: 1,
    damage: 16,
    adsFov: 78,
    adsSensScale: 0.85,
    recoilKick: 0.04,
    recoilRecovery: 14,
    automatic: false,
    requiresReload: false,
    requiresPump: false,
  },
  gl: {
    id: 'gl',
    name: 'Grenade Launcher',
    magSize: 6,
    reserveStart: 12,
    fireInterval: 0.9,
    reloadTime: 2.1,
    pumpTime: 0,
    hipSpreadDeg: 1.2,
    adsSpreadDeg: 0.4,
    pellets: 1,
    damage: 85,
    adsFov: 64,
    adsSensScale: 0.6,
    recoilKick: 0.16,
    recoilRecovery: 7,
    automatic: false,
    requiresReload: false,
    requiresPump: false,
  },
  rpg: {
    id: 'rpg',
    name: 'Rocket Launcher',
    magSize: 1,
    reserveStart: 4,
    fireInterval: 0.4,
    reloadTime: 0.34,
    pumpTime: 0,
    hipSpreadDeg: 0.8,
    adsSpreadDeg: 0.25,
    pellets: 1,
    damage: 130,
    adsFov: 60,
    adsSensScale: 0.55,
    recoilKick: 0.2,
    recoilRecovery: 6,
    automatic: false,
    requiresReload: true,
    requiresPump: false,
  },
};

export const WEAPON_ORDER: WeaponId[] = ['punch', 'pistol', 'sawedoff', 'shotgun', 'mp5', 'ak47', 'gl', 'rpg'];

/** Digit keys 1–7 (HUD slot order). First id in a slot wins if several are owned. */
export const WEAPON_SLOTS: Array<{ slot: number; ids: WeaponId[] }> = [
  { slot: 1, ids: ['punch'] },
  { slot: 2, ids: ['pistol'] },
  { slot: 3, ids: ['sawedoff', 'shotgun'] },
  { slot: 4, ids: ['mp5'] },
  { slot: 5, ids: ['ak47'] },
  { slot: 6, ids: ['gl'] },
  { slot: 7, ids: ['rpg'] },
];

export function pickSlotWeapon(ids: WeaponId[], owned: (id: WeaponId) => boolean): WeaponId | null {
  for (const id of ids) {
    if (owned(id)) return id;
  }
  return null;
}

export const WEAPON_HUD_COLOR: Record<WeaponId, string> = {
  pistol: '#d4a84a',
  shotgun: '#8aaa4a',
  mp5: '#4aa8c4',
  ak47: '#c46a32',
  sawedoff: '#c49448',
  punch: '#9aa0a8',
  gl: '#5aaa4a',
  rpg: '#d05040',
};

/** Weapons the player starts with (Facility: pistol + fists). */
export const START_OWNED_WEAPONS: WeaponId[] = ['pistol', 'punch'];

export function isShotgunLike(id: WeaponId): boolean {
  return id === 'shotgun' || id === 'sawedoff';
}

export function isMeleeWeapon(id: WeaponId): id is 'punch' {
  return id === 'punch';
}

export function isLauncherWeapon(id: WeaponId): id is 'gl' | 'rpg' {
  return id === 'gl' || id === 'rpg';
}

/** Extra barrel / launch pitch (radians) so the grenade follows the tube. */
export const GL_BARREL_LOFT = 0.24;
